import React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import ThemeMaker from "../../components/thememaker";
import axios from "axios";
import { tokenDecode } from "../../utils/helper";
import { config } from "../../utils/config";
import { Paginated } from "../../components/Paginated";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CloseIcon from '@mui/icons-material/Close';

const { apiEndpoint } = config;

const NO_IMAGE_URL = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Crect width='60' height='60' fill='%23F1F5F9' rx='8'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394A3B8' font-size='10' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E";

const Ondclistedproduct = () => {
    const [productList, setproductList] = useState<any[]>([]);
    const [allData, setallData] = useState<any[]>([]);
    const [inputText, setinputText] = useState<string>("");
    const [startDate, setstartDate] = useState<string>("");
    const [endDate, setendDate] = useState<string>("");
    const [appliedStartDate, setAppliedStartDate] = useState<string>("");
    const [appliedEndDate, setAppliedEndDate] = useState<string>("");
    const [loading, setloading] = useState<boolean>(true);

    const decode = useMemo(() => tokenDecode(), []);

    const getProductList = useCallback(async (start?: string, end?: string) => {
        setloading(true);
        try {
            const res = await axios({
                url: `${apiEndpoint}/snp/products/get-product-sold`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem("token")}`,
                },
                params: {
                    "start_date": start || undefined,
                    "end_date": end || undefined
                },
            });
            // console.log("Product Sold Response:", res?.data);
            const apiRes = res?.data || {};
            if (apiRes?.status === false) {
                setproductList([]);
                setallData([]);
                return;
            }

            const rawProducts = apiRes?.products || (Array.isArray(apiRes) ? apiRes : apiRes?.data) || [];

            if (!Array.isArray(rawProducts) || rawProducts.length === 0) {
                setproductList([]);
                setallData([]);
                return;
            }

            const formatted = rawProducts.map((item: any, index: number) => {
                const details = item?.product_details || {};
                const priceObj = (item?.prices && item?.prices[0]) ? item?.prices[0] : {};
                const measureAttr = (item?.attributes || []).find((a: any) => a.attribute_name === "measure");
                const uniqueRowId = `var_${item?.product_id}_${item?.variant_id || index}`;

                return {
                    ...item,
                    id: uniqueRowId,
                    sn: index + 1,
                    product_name: details?.name || item?.name || "N/A",
                    order_quantity: item?.quantity_sold ?? item?.order_quantity ?? 0,
                    image: details?.image || details?.logo || item?.image || NO_IMAGE_URL,
                    selling_price: priceObj?.selling_price || "-",
                    mrp: priceObj?.mrp || "-",
                    measure: measureAttr?.attribute_value || "-",
                    product_type_name: details?.product_type_name || details?.category_id || "-",
                    ondc_product_id: details?.ondc_product_id || item?.ondc_product_variant_id || "-",
                };
            });

            setproductList(formatted);
            setallData(formatted);
        } catch (error) {
            console.log("Get Product Sold Error:", error);
            setproductList([]);
            setallData([]);
        } finally {
            setloading(false);
        }
    }, []);

    useEffect(() => {
        getProductList();
    }, [getProductList]);

    const displayList = useMemo(() => {
        if (!allData || !Array.isArray(allData)) return [];
        if (!inputText) return allData;
        const query = inputText.toLowerCase().trim();
        return allData.filter((item: any) =>
            item?.product_name?.toLowerCase()?.trim()?.includes(query)
        );
    }, [inputText, allData]);


    const COLUMNS = useMemo(() => [
        {
            Header: "Serial No.",
            Footer: "Serial No.",
            id: "serial_no",
            accessor: "sn",
            Cell: ({ row }: any) => (
                <span className="font-weight-bold" style={{ color: "#64748B" }}>
                    {row.index + 1}
                </span>
            ),
            disableFilters: true,
        },
        {
            Header: "Product Image",
            Footer: "Product Image",
            accessor: "image",
            Cell: ({ value }: any) => (
                <img
                    src={value || NO_IMAGE_URL}
                    alt="Product"
                    height={52}
                    width={52}
                    style={{ objectFit: "cover", borderRadius: "10px", border: "1px solid #E2E8F0", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}
                    onError={(e: any) => {
                        e.target.onerror = null;
                        e.target.src = NO_IMAGE_URL;
                    }}
                />
            ),
        },
        {
            Header: "Product Name",
            Footer: "Product Name",
            accessor: "product_name",
            Cell: ({ row }: any) => {
                const a = row.original;
                return (
                    <div>
                        <div className="font-weight-bold" style={{ color: "#0F172A", fontSize: "14px" }}>{a?.product_name}</div>
                        {a?.measure && a?.measure !== "-" && (
                            <small style={{ color: "#64748B", fontWeight: 500 }}>Measure: {a?.measure}</small>
                        )}
                    </div>
                );
            },
        },
        {
            Header: "Category / Type",
            Footer: "Category / Type",
            accessor: "product_type_name",
            Cell: ({ value }: any) => (
                <span className="badge" style={{ backgroundColor: "#F1F5F9", color: "#334155", padding: "6px 12px", borderRadius: "8px", fontWeight: 600, fontSize: "12px" }}>
                    {value || "-"}
                </span>
            ),
        },
        {
            Header: "Selling Price",
            Footer: "Selling Price",
            accessor: "selling_price",
            Cell: ({ value }: any) => (
                <span className="font-weight-bold" style={{ color: "#0F172A", fontSize: "14px" }}>
                    {value && value !== "-" ? `₹${value}` : "-"}
                </span>
            ),
        },
        {
            Header: "Quantity Sold",
            Footer: "Quantity Sold",
            accessor: "order_quantity",
            Cell: ({ value }: any) => (
                <span style={{ backgroundColor: "#FFF7ED", color: "#C2410C", border: "1px solid #FFEDD5", padding: "6px 14px", borderRadius: "20px", fontWeight: 700, fontSize: "13px", display: "inline-block" }}>
                    {value ?? 0}
                </span>
            ),
        },
    ], []);



    const [Fopen, setFOpen] = useState(false);
    const handleFClose = () => setFOpen(false);
    const handleFOpen = () => {
        setstartDate(appliedStartDate);
        setendDate(appliedEndDate);
        setFOpen(true);
    };

    const handleApplyFilter = () => {
        if (!startDate || !endDate) {
            toast.warn("Please select both start date and end date");
            return;
        }
        if (startDate > endDate) {
            toast.error("Start date cannot be greater than end date");
            return;
        }
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        getProductList(startDate, endDate);
        handleFClose();
    };

    const handleClearFilter = () => {
        setstartDate("");
        setendDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        getProductList("", "");
        handleFClose();
    };

    function getCurrentDate() {
        const today = new Date();
        const year = today.getFullYear();
        let month: string | number = today.getMonth() + 1;
        let day: string | number = today.getDate();

        month = month < 10 ? `0${month}` : month;
        day = day < 10 ? `0${day}` : day;

        return `${year}-${month}-${day}`;
    }

    function sortHandler(e: React.ChangeEvent<HTMLSelectElement>) {
        const mode = e.target.value;
        if (!allData || allData.length === 0) return;

        let z = [...allData];
        if (mode === "lowtohigh") {
            z.sort((a, b) => {
                const diff = (Number(a.order_quantity) || 0) - (Number(b.order_quantity) || 0);
                if (diff !== 0) return diff;
                return String(a.product_name || "").localeCompare(String(b.product_name || ""));
            });
            setallData(z);
        } else if (mode === "hightoLow") {
            z.sort((a, b) => {
                const diff = (Number(b.order_quantity) || 0) - (Number(a.order_quantity) || 0);
                if (diff !== 0) return diff;
                return String(a.product_name || "").localeCompare(String(b.product_name || ""));
            });
            setallData(z);
        }
    }

    return (
        <>
            <ThemeMaker>
                <div className="row w-100 m-0 mt-2">
                    <div className="col-md-12">
                        <div className="card shadow-sm border p-4 mb-4" style={{ borderRadius: "20px", borderColor: "#E5E7EB", backgroundColor: "#ffffff", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)", position: "sticky", top: "10px", zIndex: 30 }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap mb-4" style={{ gap: "15px" }}>
                                <div className="d-flex align-items-center" style={{ gap: "14px", flexShrink: 0 }}>
                                    <div style={{ backgroundColor: "rgba(249, 115, 22, 0.1)", borderRadius: "14px", width: "48px", height: "48px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <i className="fas fa-box-open" style={{ color: "#F97316", fontSize: "22px" }}></i>
                                    </div>
                                    <div>
                                        <h4 className="font-weight-bold m-0" style={{ color: "#0F172A", fontSize: "22px", letterSpacing: "-0.5px" }}>
                                            ONDC Products Sold List
                                        </h4>
                                        <span style={{ fontSize: "13px", color: "#64748B" }}>
                                            Track sold items, quantity counts, and sales performance
                                        </span>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center justify-content-end flex-wrap ml-auto" style={{ gap: "10px" }}>
                                    <div
                                        className="d-flex align-items-center"
                                        style={{
                                            border: "1.5px solid #F97316",
                                            borderRadius: "10px",
                                            height: "46px",
                                            padding: "0 14px",
                                            backgroundColor: "#FFFFFF",
                                            boxSizing: "border-box"
                                        }}
                                    >
                                        <i className="fas fa-search mr-2" style={{ fontSize: "14px", color: "#F97316" }}></i>
                                        <input
                                            type="text"
                                            placeholder="Search product..."
                                            className="border-0 bg-transparent text-dark"
                                            value={inputText}
                                            onChange={(e) => setinputText(e.target.value)}
                                            style={{ outline: "none", fontSize: "14px", width: "190px", padding: 0 }}
                                        />
                                        {inputText && (
                                            <i
                                                className="fas fa-times"
                                                style={{ color: "#94A3B8", cursor: "pointer", fontSize: "12px", marginLeft: "6px" }}
                                                onClick={() => setinputText("")}
                                            ></i>
                                        )}
                                    </div>

                                    <button
                                        className="btn font-weight-bold d-flex align-items-center"
                                        style={{
                                            borderRadius: "10px",
                                            height: "46px",
                                            padding: "0 16px",
                                            fontSize: "14px",
                                            gap: "8px",
                                            border: appliedStartDate && appliedEndDate ? "1.5px solid #F97316" : "1.5px solid #CBD5E1",
                                            backgroundColor: appliedStartDate && appliedEndDate ? "#FFF7ED" : "#FFFFFF",
                                            color: appliedStartDate && appliedEndDate ? "#EA580C" : "#334155",
                                            boxSizing: "border-box"
                                        }}
                                        onClick={handleFOpen}
                                        title={appliedStartDate && appliedEndDate ? `${appliedStartDate} to ${appliedEndDate}` : "Filter"}
                                    >
                                        <i className="fas fa-filter" style={{ color: "#F97316" }}></i>
                                        Filter
                                        {appliedStartDate && appliedEndDate && (
                                            <span style={{ backgroundColor: "#F97316", color: "#FFFFFF", borderRadius: "20px", padding: "1px 7px", fontSize: "11px", fontWeight: "bold" }}>
                                                Active
                                            </span>
                                        )}
                                    </button>

                                    {appliedStartDate && appliedEndDate && (
                                        <button
                                            className="btn font-weight-bold d-flex align-items-center"
                                            style={{
                                                borderRadius: "10px",
                                                height: "46px",
                                                padding: "0 14px",
                                                fontSize: "13px",
                                                gap: "6px",
                                                border: "1.5px solid #FECACA",
                                                backgroundColor: "#FEF2F2",
                                                color: "#DC2626",
                                                boxSizing: "border-box"
                                            }}
                                            onClick={handleClearFilter}
                                            title="Remove date filter"
                                        >
                                            <i className="fas fa-times"></i>
                                            Remove Filter
                                        </button>
                                    )}



                                </div>
                            </div>

                            {!loading ? (
                                <div className="table-border overflow-hidden" style={{ borderRadius: "16px", border: "1px solid #E5E7EB" }}>
                                    <Paginated data={displayList as any} columns={COLUMNS as any} emptyMessage="Sold products are not found!" />
                                </div>
                            ) : (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
                                    <CircularProgress style={{ color: "#F97316" }} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </ThemeMaker>

            <Modal open={Fopen}
                onClose={handleFClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description">

                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content" style={{ borderRadius: "16px", border: "none", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}>
                        <div className="modal-header d-flex justify-content-between align-items-center" style={{ borderBottom: "1px solid #F1F5F9", padding: "18px 24px" }}>
                            <h5 className="modal-title font-weight-bold text-dark m-0" id="exampleModalLongTitle"> Filter Products </h5>
                            <span aria-hidden="true" onClick={handleFClose} style={{ cursor: "pointer", color: "#64748B" }}><CloseIcon /></span>
                        </div>

                        <div className="modal-body" style={{ padding: "24px" }}>
                            <div className="d-flex flex-wrap" style={{ gap: "20px" }}>
                                <div className="flex-fill">
                                    <label className="font-weight-bold" style={{ fontSize: "13px", color: "#475569" }}>Start Date</label>
                                    <input
                                        name="startDate"
                                        type="date"
                                        className="form-control"
                                        style={{ borderRadius: "10px", borderColor: "#CBD5E1", height: "42px" }}
                                        value={startDate}
                                        onChange={(e) => setstartDate(e.target.value)}
                                        max={getCurrentDate()}
                                    />
                                </div>

                                <div className="flex-fill">
                                    <label className="font-weight-bold" style={{ fontSize: "13px", color: "#475569" }}>End Date</label>
                                    <input
                                        name="endDate"
                                        type="date"
                                        className="form-control"
                                        style={{ borderRadius: "10px", borderColor: "#CBD5E1", height: "42px" }}
                                        value={endDate}
                                        onChange={(e) => setendDate(e.target.value)}
                                        max={getCurrentDate()}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer d-flex justify-content-end" style={{ borderTop: "1px solid #F1F5F9", padding: "16px 24px", gap: "12px" }}>
                            <button
                                className="btn font-weight-bold"
                                style={{ borderRadius: "10px", backgroundColor: "#F1F5F9", color: "#475569", padding: "8px 18px", border: "none" }}
                                onClick={handleClearFilter}
                            >
                                Clear Filter
                            </button>

                            <button
                                className="btn text-white font-weight-bold"
                                style={{ borderRadius: "10px", backgroundColor: "#F97316", padding: "8px 22px", border: "none" }}
                                onClick={handleApplyFilter}
                            >
                                Apply Filter
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default Ondclistedproduct;
